import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Youtube, Mail, Twitch, Instagram, ArrowUpRight, Send } from 'lucide-react';
import { streamer, testimonials } from '../mock';
import { toast } from '../hooks/use-toast';
import { api } from '../lib/api';

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7.02a4.85 4.85 0 0 1-1.84-.33Z" />
  </svg>
);

const KickIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M3 3h5v5h2V5h2V3h5v5h-2v2h-2v4h2v2h2v5h-5v-3h-2v-2H8v5H3V3Z" />
  </svg>
);

const cards = [
  {
    key: 'discord',
    title: 'Discord',
    desc: 'Mis niks van dit kanaal en blijf op de hoogte van alle updates.',
    cta: 'Join Discord',
    icon: MessageCircle,
    color: 'from-indigo-500 to-violet-600',
    href: streamer.socials.discord
  },
  {
    key: 'twitch',
    title: 'Twitch',
    desc: 'Volg het kanaal en mis nooit een live stream.',
    cta: 'Volg op Twitch',
    icon: Twitch,
    color: 'from-violet-500 to-fuchsia-600',
    href: streamer.socials.twitch
  },
  {
    key: 'youtube',
    title: 'YouTube',
    desc: 'Liever een korte samenvatting? Check het YouTube kanaal.',
    cta: 'Open YouTube',
    icon: Youtube,
    color: 'from-rose-500 to-red-600',
    href: streamer.socials.youtube
  },
  {
    key: 'tiktok',
    title: 'TikTok',
    desc: 'Korte clips, fails en de beste momenten uit de stream.',
    cta: 'Volg op TikTok',
    icon: TikTokIcon,
    color: 'from-cyan-400 to-pink-500',
    href: streamer.socials.tiktok
  },
  {
    key: 'instagram',
    title: 'Instagram',
    desc: 'Achter-de-schermen en updates uit het dagelijks leven.',
    cta: 'Volg op Instagram',
    icon: Instagram,
    color: 'from-fuchsia-500 to-orange-500',
    href: streamer.socials.instagram
  },
  {
    key: 'kick',
    title: 'Kick',
    desc: 'Backup-streams of co-streams? Check ook even Kick.',
    cta: 'Volg op Kick',
    icon: KickIcon,
    color: 'from-emerald-400 to-green-600',
    href: streamer.socials.kick
  }
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Vul alle velden in', description: 'Naam, email en bericht zijn verplicht.' });
      return;
    }
    try {
      await api.post('/contact', form);
      toast({
        title: 'Bericht verstuurd! \u{1F389}',
        description: `Bedankt ${form.name}, ik reageer zo snel mogelijk.`
      });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast({ title: 'Oeps', description: 'Versturen mislukt, probeer opnieuw.' });
    }
  };

  return (
    <section id="contact" className="relative py-24 px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
            Connect met de community
          </h2>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto">
            Discord, Twitch, YouTube, TikTok, Instagram of Kick — aan jou de keuze.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-16">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.key}
                href={c.href}
                target="_blank"
                rel="noreferrer"
                className="group relative rounded-2xl p-6 bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all hover:-translate-y-1 overflow-hidden"
              >
                <div
                  className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br ${c.color} opacity-15 blur-2xl group-hover:opacity-30 transition-opacity`}
                />
                <div className={`relative inline-flex w-12 h-12 rounded-xl bg-gradient-to-br ${c.color} items-center justify-center mb-5`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">{c.title}</h3>
                <p className="text-zinc-400 mt-2 text-sm">{c.desc}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-violet-300 font-semibold text-sm group-hover:gap-3 transition-all">
                  {c.cta} <ArrowUpRight className="w-4 h-4" />
                </div>
              </a>
            );
          })}
        </div>

        <div className="-mt-8 mb-16 text-center">
          <Link
            to="/socials"
            data-testid="contact-all-socials-link"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/30 text-white font-semibold text-sm transition-all"
          >
            Bekijk alle socials <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Contact form */}
          <div id="contact-form" className="lg:col-span-3 rounded-2xl p-6 md:p-8 bg-white/[0.03] border border-white/10 scroll-mt-24">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-violet-300" />
              <h3 className="text-2xl font-bold text-white">Stuur een bericht</h3>
            </div>
            <p className="text-zinc-400 text-sm mb-6">
              Sponsoring, samenwerking of gewoon een hi? Drop een berichtje.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400">Naam</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-zinc-900/80 border border-white/10 text-white focus:outline-none focus:border-violet-500/60 transition-colors"
                    placeholder="Jouw naam"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-400">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-zinc-900/80 border border-white/10 text-white focus:outline-none focus:border-violet-500/60 transition-colors"
                    placeholder="jij@email.nl"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">Bericht</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-zinc-900/80 border border-white/10 text-white focus:outline-none focus:border-violet-500/60 transition-colors resize-none"
                  placeholder="Vertel maar..."
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-colors"
              >
                <Send className="w-4 h-4" /> Verstuur bericht
              </button>
            </form>
          </div>

          {/* Testimonials */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-lg font-semibold text-white">Wat de community zegt</h3>
            {testimonials.map((t) => (
              <div
                key={t.name}
                className="rounded-xl p-5 bg-white/[0.03] border border-white/10"
              >
                <p className="text-zinc-200 text-sm leading-relaxed">“{t.text}”</p>
                <div className="flex items-center gap-3 mt-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-9 h-9 rounded-full border border-white/10"
                  />
                  <span className="text-sm font-semibold text-violet-300">{t.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
